import { BUCKETS } from "../model/schedule.ts";
import type { BucketKey, PlanInputs, SolveResult } from "../model/types.ts";
import { crore, money, multiple, pct, usd } from "../lib/format.ts";
import { InfoTip } from "./InfoTip.tsx";
import { ON_TRACK_BAND, gapOf } from "../lib/gap.ts";

const LABELS: Record<BucketKey, string> = {
  life: "Lifestyle",
  undergrad: "Undergraduate education",
  school: "Schooling",
  elder: "Parents' care",
  health: "Health cover",
  access: "Getting to services",
};

export interface SavingsSummary {
  grossInr: number;
  grossUsd: number;
  netInr: number;
  fxRate: number;
  saleYear: number;
  isExample: boolean;
  /** Other savings counted towards the corpus (today's value, before tax). */
  otherCounted: number;
  /** NPS shown but left out because it is locked at this retirement age. */
  npsExcluded: number;
}

export function Results({
  result,
  inputs,
  busy,
  savings,
  retireYear,
  onEditSavings,
}: {
  result: SolveResult;
  inputs: PlanInputs;
  busy: boolean;
  savings: SavingsSummary;
  retireYear: number;
  onEditSavings: () => void;
}) {
  const target = result.corpus["0.90"];
  const bucketTotal = BUCKETS.reduce((a, k) => a + result.bucketCost[k], 0);
  const ordered = [...BUCKETS].sort((a, b) => result.bucketCost[b] - result.bucketCost[a]);

  return (
    <section className={busy ? "results busy" : "results"} aria-busy={busy}>
      <div className="tiles">
        <div className="tile primary">
          <div className="label">
            <InfoTip label="Corpus needed, 90% confidence" trackAs="corpus">
              The investable money you need on the day you retire, not counting the home you live
              in. In 90 of every 100 simulated market histories, this amount pays every expense and
              its tax for all {inputs.horizonYears} years without running out.
            </InfoTip>
          </div>
          <div className="value">{crore(target)}</div>
          <div className="sub">
            On day one of retirement, investable. Excludes the home you live in.
          </div>
        </div>
        <div className="tile">
          <div className="label">
            <InfoTip label="Year-one outflow" trackAs="year-one-outflow">
              Everything you take out of the portfolio in your first year of retirement: living costs,
              schooling, university, parents' care, health cover and travel to reach services, plus
              the tax on those withdrawals. Shown in rupees of your retirement year, so it includes
              inflation between now and then.
            </InfoTip>
          </div>
          <div className="value">{money(result.yearOneGross)}</div>
          <div className="sub">Everything, including tax — not just the lifestyle number.</div>
        </div>
        <div className="tile">
          <div className="label">
            <InfoTip label="Implied withdrawal rate" trackAs="withdrawal-rate">
              Year-one outflow divided by the corpus: the share of your savings you spend in the first
              year. The well-known 4% rule comes from 30-year US retirements; higher inflation, tax on
              withdrawals and a longer horizon push the safe rate for India lower.
            </InfoTip>
          </div>
          <div className="value">{pct(result.impliedSwr, 2)}</div>
          <div className="sub">
            Not 4%. India-specific research lands at 2.6–3.2% over 30 years; a longer horizon
            pushes it lower.
          </div>
        </div>
        <div className="tile">
          <div className="label">
            <InfoTip label="Corpus multiple" trackAs="multiple">
              The corpus divided by year-one outflow: how many years of first-year spending you need
              saved. It is the withdrawal rate turned upside down, so 2.5% means 40×.
            </InfoTip>
          </div>
          <div className="value">{multiple(result.multiple)}</div>
          <div className="sub">Of year-one outflow, not of headline lifestyle spend.</div>
        </div>
      </div>

      <SavingsGap
        corpus={target}
        savings={savings}
        retireYear={retireYear}
        onEditSavings={onEditSavings}
      />

      <table className="confidence">
        <caption>How much certainty costs</caption>
        <thead>
          <tr>
            <th scope="col">Confidence</th>
            <th scope="col">Corpus</th>
            <th scope="col">vs 90%</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(result.corpus)
            .sort(([a], [b]) => +a - +b)
            .map(([conf, value]) => (
              <tr key={conf} className={conf === "0.90" ? "highlight" : undefined}>
                <th scope="row">{pct(+conf, 0)} of paths survive</th>
                <td>{crore(value)}</td>
                <td>
                  {conf === "0.90" ? "—" : `${value > target ? "+" : ""}${crore(value - target)}`}
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      <table className="buckets">
        <caption>Where the money goes</caption>
        <thead>
          <tr>
            <th scope="col">Bucket</th>
            <th scope="col">Cost</th>
            <th scope="col">Share</th>
          </tr>
        </thead>
        <tbody>
          {ordered.map((k) => (
            <tr key={k}>
              <th scope="row">
                <i className={`swatch area-${k}`} />
                {LABELS[k]}
              </th>
              <td>{crore(result.bucketCost[k], 2)}</td>
              <td>{pct(result.bucketCost[k] / bucketTotal, 0)}</td>
            </tr>
          ))}
          <tr className="subtotal">
            <th scope="row">Cost if returns arrive on average</th>
            <td>{crore(result.deterministic, 2)}</td>
            <td>100%</td>
          </tr>
          <tr className="highlight">
            <th scope="row">Buffer for retiring into an unknown decade</th>
            <td>+{crore(target - result.deterministic, 2)}</td>
            <td>+{pct(result.sequenceRiskPremium, 0)}</td>
          </tr>
        </tbody>
      </table>

      <p className="note">
        That last row is the whole argument against planning off average returns. The order in which
        good and bad years arrive matters more than the average, because a bad first decade forces
        you to sell into it.
      </p>

      <p className="meta">
        {inputs.simulations.toLocaleString("en-IN")} simulated paths over {inputs.horizonYears} years,
        solved in {(result.elapsedMs / 1000).toFixed(1)}s
        {inputs.guardrails ? ", spending guardrails on" : ", no spending guardrails"}.
      </p>
    </section>
  );
}

/** Corpus needed against all savings: US accounts plus other savings. */
function SavingsGap({
  corpus,
  savings,
  retireYear,
  onEditSavings,
}: {
  corpus: number;
  savings: SavingsSummary;
  retireYear: number;
  onEditSavings: () => void;
}) {
  const totalGross = savings.grossInr + savings.otherCounted;
  const totalNet = savings.netInr + savings.otherCounted;
  const gross = gapOf(corpus, totalGross);
  const net = gapOf(corpus, totalNet);
  const bar = (v: number) => `${Math.min(1, Math.max(0, v)) * 100}%`;

  return (
    <section className="gap" aria-labelledby="gap-title">
      <div className="gap-head">
        <h3 id="gap-title">
          Corpus needed vs your savings
          {savings.isExample && <span className="badge">Example US figures</span>}
        </h3>
        <button type="button" className="link" onClick={onEditSavings}>
          {savings.isExample ? "Enter my US savings" : "Edit US savings"}
        </button>
      </div>

      <dl className="gap-sum">
        <div>
          <dt>Corpus needed (90%)</dt>
          <dd>{crore(corpus, 2)}</dd>
        </div>
        <div>
          <dt>US savings, gross</dt>
          <dd>
            − {crore(savings.grossInr, 2)}
            <small>
              {usd(savings.grossUsd)} at ₹{savings.fxRate.toFixed(2)}
            </small>
          </dd>
        </div>
        <div>
          <dt>Other savings, today's value</dt>
          <dd>
            − {crore(savings.otherCounted, 2)}
            <small>{savings.otherCounted > 0 ? "before tax" : "none entered yet"}</small>
          </dd>
        </div>
        <div className={`gap-total ${gross.status}`}>
          <dt>{gross.label}</dt>
          <dd>{crore(gross.amount, 2)}</dd>
        </div>
      </dl>

      <div
        className="gap-bar"
        role="img"
        aria-label={`Savings cover ${pct(gross.coverage, 0)} of the corpus; with US savings after tax and transfer costs, ${pct(net.coverage, 0)}.`}
      >
        <span className="gap-bar-gross" style={{ width: bar(gross.coverage) }} />
        <span className="gap-bar-net" style={{ width: bar(net.coverage) }} />
      </div>
      <p className="gap-legend">
        <span><i className="gap-key-net" /> US after tax &amp; transfer: {pct(net.coverage, 0)}</span>
        <span><i className="gap-key-gross" /> Before tax: {pct(gross.coverage, 0)} of the corpus</span>
      </p>

      <p className="gap-note">
        {savings.isExample && (
          <>
            <strong>The US savings are example figures.</strong> Enter your own on the USD → INR
            transfer tab.{" "}
          </>
        )}
        After US tax, penalties, transfer costs and Indian tax, your US savings land as about{" "}
        <strong>{crore(savings.netInr, 2)}</strong>, which makes it{" "}
        {net.status === "on-track" ? (
          <strong>on track</strong>
        ) : (
          <>
            a {net.label.toLowerCase()} of <strong>{crore(net.amount, 2)}</strong>
          </>
        )}
        . Other savings are
        counted at today's value before tax; Indian gains are taxed when you sell, so the real
        figure is a little lower.{" "}
        {savings.npsExcluded > 0 &&
          `NPS of ${crore(savings.npsExcluded, 2)} is not counted because it is largely locked until 60. `}
        "On track" means within {Math.round(ON_TRACK_BAND * 100)}% of the corpus.{" "}
        {savings.saleYear !== retireYear &&
          `US savings are valued in ${savings.saleYear} and the corpus is needed in ${retireYear}, so treat the difference as approximate.`}
      </p>
    </section>
  );
}
